import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, asOverrideKey } from "@/lib/api-client";
import { searchKeys } from "@/lib/query";

const PolicySearchResultSchema = z.array(
  z.object({
    policyNumber: z.string(),
    numberName: z.string(),
  }),
);

const AgencySearchResultSchema = z.array(
  z.object({
    agentNumber: z.string(),
    nameNumber: z.string(),
  }),
);

const ClaimSearchResultSchema = z.array(
  z.object({
    claimNumber: z.string(),
    nameNumber: z.string(),
  }),
);

const SearchTypeResultSchema = z.array(
  z.object({
    id: z.string(),
    searchType: z.string(),
    isActive: z.boolean(),
  }),
);

// Build a `/api/search/:domain?q=...` URL. The three search domains share the
// same call shape; the only per-domain difference is which schema validates
// the response.
function searchUrl(domain: "policies" | "agencies" | "claims", query: string) {
  return `/api/search/${domain}?q=${encodeURIComponent(query)}`;
}

export function useSearchPolicies(query: string) {
  return useQuery({
    queryKey: searchKeys.policies(query),
    queryFn: () => apiGet(searchUrl("policies", query), PolicySearchResultSchema),
    enabled: query.trim().length > 0,
  });
}

export function useSearchAgencies(query: string) {
  return useQuery({
    queryKey: searchKeys.agencies(query),
    queryFn: () => apiGet(searchUrl("agencies", query), AgencySearchResultSchema),
    enabled: query.trim().length > 0,
  });
}

// Claims search respects the impersonation override on the cache key so admins
// switching identity see fresh results instead of the prior identity's hits.
export function useSearchClaims(query: string) {
  return useQuery({
    queryKey: [...searchKeys.claims(query), asOverrideKey()],
    queryFn: () => apiGet(searchUrl("claims", query), ClaimSearchResultSchema),
    enabled: query.trim().length > 0,
  });
}

export function useSearchTypes() {
  return useQuery({
    queryKey: searchKeys.types,
    queryFn: () => apiGet("/api/search-types", SearchTypeResultSchema),
  });
}
